import { type KeyboardEvent, type PointerEvent, type RefObject, useCallback, useEffect, useRef, useState } from "react";
import type * as api from "./api";
import { useCone } from "./cone";
import {
  clampView,
  CONE_RANGES,
  crossAt,
  crossWords,
  dataDomain,
  dateAt,
  dayAt,
  DEFAULT_PAST,
  defaultView,
  historyNote,
  panView,
  rangeView,
  selectView,
  type View,
  zoomView,
} from "./coneView";
import { crossLines, FillCone, layoutFrame } from "./FillCone";
import { canFullscreen, FULLSCREEN_EVENTS, fullscreenOf, toggleFullscreen } from "./MapFullscreen";
import { useMedia } from "./Menu";

const BASE_WIDTH = 720;
const WIDE_HEIGHT = 300;
const NARROW_HEIGHT = 260;
const FULL_ROOM = 190;
const DRAG_PX = 4;
const ZOOM_STEP = 0.6;
const PAN_SHARE = 0.25;
const WHEEL_RATE = 0.0025;
const LINE_PX = 16;

export const HELP_TEXT = "Drag across the chart to zoom in. Shift + drag or Shift + scroll moves it. Ctrl + scroll zooms at the pointer.";
export const TOUCH_TEXT = "Pinch to zoom. Swipe sideways to move. Tap for the value of a day.";
export const TOUCH_FIRST_TEXT = "Pinch to zoom, then swipe sideways to move. Tap for the value of a day.";
export const KEY_TEXT = "The arrow keys move the crosshair one day; + and - zoom.";

/** The fullscreen state of the chart box (see useMapFullscreen). */
function useBoxFullscreen(box: RefObject<HTMLElement>) {
  const [on, setOn] = useState(false);
  const [supported] = useState(() => typeof document !== "undefined" && canFullscreen(document));
  useEffect(() => {
    const changed = () => {
      const now = fullscreenOf(document) !== null && fullscreenOf(document) === box.current;
      setOn(now);
      console.info(`fill chart fullscreen: ${now ? "on" : "off"}`);
    };
    for (const name of FULLSCREEN_EVENTS) document.addEventListener(name, changed);
    return () => {
      for (const name of FULLSCREEN_EVENTS) document.removeEventListener(name, changed);
    };
  }, [box]);
  const toggle = useCallback(() => {
    if (box.current) toggleFullscreen(document, box.current).catch((e: unknown) => console.warn("fill chart fullscreen: the browser refused", e));
  }, [box]);
  return { on, supported, toggle };
}

/** The width of the box, and the height of the window in fullscreen. */
function useBoxSize(box: RefObject<HTMLElement>, full: boolean) {
  const [size, setSize] = useState({ width: BASE_WIDTH, screen: 0 });
  useEffect(() => {
    const element = box.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const measure = () => setSize({ width: Math.max(280, Math.round(element.clientWidth)), screen: window.innerHeight });
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(element);
    return () => watch.disconnect();
  }, [box, full]);
  const height = full ? Math.max(WIDE_HEIGHT, size.screen - FULL_ROOM) : size.width < 560 ? NARROW_HEIGHT : WIDE_HEIGHT;
  return { width: size.width, height };
}

const Icon = ({ d }: { d: string }) => (
  <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
    <path d={d} />
  </svg>
);

interface Drag {
  mode: "select" | "pan" | "pinch";
  touch: boolean;
  startPx: number;
  startDay: number;
  startView: View;
  moved: boolean;
  dist: number;
  midDay: number;
}

/** The zoomable fill chart of the account page (SO-4319): quick ranges, zoom, pan, fullscreen and a crosshair. */
export function ConeChart({
  cone,
  now = Date.now(),
  loading = false,
  failed = false,
  onPast,
}: {
  cone: api.FillCone;
  now?: number;
  loading?: boolean;
  failed?: boolean;
  onPast?: (days: number) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const plot = useRef<HTMLDivElement>(null);
  const full = useBoxFullscreen(box);
  const { width, height } = useBoxSize(plot, full.on);
  const touch = useMedia("(pointer: coarse)");
  const domain = dataDomain(cone, now);
  const [stored, setStored] = useState<View | null>(null);
  const view = clampView(stored ?? defaultView(domain), domain);
  const [range, setRange] = useState<string | null>(null);
  const [cursor, setCursor] = useState<number | null>(null);
  const [selection, setSelection] = useState<View | null>(null);
  const [said, setSaid] = useState("");
  const pending = useRef<number | null>(null);
  const drag = useRef<Drag | null>(null);
  const pointers = useRef(new Map<number, number>());
  const frame = layoutFrame(width, height);
  const left = frame.left;
  const right = width - 10;
  const live = useRef({ view, domain, left, right, width });
  live.current = { view, domain, left, right, width };
  const ranges = cone.past !== undefined;

  const show = useCallback((next: View) => {
    live.current.view = next;
    setStored(next);
  }, []);

  useEffect(() => {
    const days = pending.current;
    if (days === null || (cone.past ?? 0) < days) return;
    pending.current = null;
    show(rangeView(days, dataDomain(cone, now)));
  }, [cone]);

  const pick = (key: string, days: number) => {
    console.info(`fill chart: range=${key} days=${days} loaded=${cone.past ?? "old-api"}`);
    setRange(key);
    if ((cone.past ?? DEFAULT_PAST) >= days) show(rangeView(days, domain));
    else {
      pending.current = days;
      onPast?.(days);
    }
  };
  const reset = () => {
    console.info("fill chart: reset");
    pending.current = null;
    setRange(null);
    setCursor(null);
    show(defaultView(domain));
  };
  const zoomBy = (factor: number) => {
    const at = cursor ?? (view.from + view.to) / 2;
    setRange(null);
    show(zoomView(view, at, factor, domain));
  };
  const panBy = (share: number) => {
    setRange(null);
    show(panView(view, share * (view.to - view.from), domain));
  };

  const pxOf = (clientX: number, element: Element) => {
    const rect = element.getBoundingClientRect();
    return (clientX - rect.left) * (live.current.width / Math.max(1, rect.width));
  };
  const dayOf = (px: number, at: View = live.current.view) => dayAt(px, left, right, at);

  useEffect(() => {
    const svg = plot.current?.querySelector("svg");
    if (!svg) return;
    const wheel = (e: WheelEvent) => {
      const { view: at, domain: room, left: from, right: to } = live.current;
      const scale = e.deltaMode === 1 ? LINE_PX : 1;
      const px = pxOf(e.clientX, svg);
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const factor = Math.exp(Math.max(-0.5, Math.min(0.5, e.deltaY * scale * WHEEL_RATE)));
        setRange(null);
        show(zoomView(at, dayAt(px, from, to, at), factor, room));
      } else if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault();
        const delta = (Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY) * scale;
        setRange(null);
        show(panView(at, (delta / Math.max(1, to - from)) * (at.to - at.from), room));
      }
    };
    svg.addEventListener("wheel", wheel, { passive: false });
    return () => svg.removeEventListener("wheel", wheel);
  }, [show, width, height]);

  const down = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const px = pxOf(e.clientX, e.currentTarget);
    pointers.current.set(e.pointerId, px);
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch (error) {
      console.warn("fill chart: no pointer capture", error);
    }
    const isTouch = e.pointerType !== "mouse";
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      drag.current = { mode: "pinch", touch: true, startPx: px, startDay: 0, startView: view, moved: true, dist: Math.max(20, Math.abs(a - b)), midDay: dayOf((a + b) / 2) };
      setSelection(null);
      return;
    }
    const mode = isTouch || e.shiftKey ? "pan" : "select";
    drag.current = { mode, touch: isTouch, startPx: px, startDay: dayOf(px), startView: view, moved: false, dist: 0, midDay: 0 };
    if (isTouch) setCursor(dayOf(px));
  };
  const move = (e: PointerEvent<HTMLDivElement>) => {
    const px = pxOf(e.clientX, e.currentTarget);
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, px);
    const d = drag.current;
    if (!d) {
      if (e.pointerType === "mouse" || e.pointerType === "pen") setCursor(dayOf(px));
      return;
    }
    if (d.mode === "pinch") {
      if (pointers.current.size < 2) return;
      const [a, b] = [...pointers.current.values()];
      const span = ((d.startView.to - d.startView.from) * d.dist) / Math.max(20, Math.abs(a - b));
      const share = ((a + b) / 2 - left) / Math.max(1, right - left);
      setRange(null);
      show(clampView({ from: d.midDay - share * span, to: d.midDay - share * span + span }, domain));
      return;
    }
    if (Math.abs(px - d.startPx) > DRAG_PX) d.moved = true;
    if (!d.moved) return;
    if (d.mode === "select") {
      setCursor(dayOf(px));
      setSelection({ from: d.startDay, to: dayOf(px) });
      return;
    }
    const days = ((d.startPx - px) / Math.max(1, right - left)) * (d.startView.to - d.startView.from);
    const next = panView(d.startView, days, domain);
    setRange(null);
    show(next);
    setCursor(d.touch ? null : dayOf(px, next));
  };
  const up = (e: PointerEvent<HTMLDivElement>, cancel = false) => {
    pointers.current.delete(e.pointerId);
    const d = drag.current;
    if (d && !cancel && d.mode === "select" && d.moved) {
      const next = selectView(d.startDay, dayOf(pxOf(e.clientX, e.currentTarget)), domain);
      console.info(`fill chart: zoom to the selection, ${(next.to - next.from).toFixed(1)} days`);
      setRange(null);
      show(next);
    } else if (d && d.moved && d.mode !== "select") console.info(`fill chart: ${d.mode}, view ${live.current.view.from.toFixed(1)} to ${live.current.view.to.toFixed(1)} days`);
    if (pointers.current.size === 0 || d?.mode === "pinch") drag.current = null;
    setSelection(null);
  };

  const key = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      e.preventDefault();
      const step = (e.key === "ArrowLeft" ? -1 : 1) * (e.shiftKey ? 7 : 1);
      const next = Math.min(Math.max((cursor ?? 0) + step, domain.from), domain.to);
      setCursor(next);
      if (next < view.from || next > view.to) show(panView(view, next < view.from ? next - view.from : next - view.to, domain));
      setSaid(crossWords(dateAt(next, now), crossAt(next, crossLines(cone, now))));
    } else if (e.key === "+" || e.key === "=") {
      e.preventDefault();
      zoomBy(ZOOM_STEP);
    } else if (e.key === "-" || e.key === "_") {
      e.preventDefault();
      zoomBy(1 / ZOOM_STEP);
    } else if (e.key === "Escape") {
      setCursor(null);
      setSelection(null);
    }
  };

  const asked = CONE_RANGES.find((item) => item.key === range);
  const note = asked && !loading && (cone.past ?? DEFAULT_PAST) >= asked.days ? historyNote(cone, now, asked.days) : null;
  const canPan = view.from > domain.from + 0.01 || view.to < domain.to - 0.01;
  const isDefault = stored === null || (Math.abs(view.from - defaultView(domain).from) < 0.01 && Math.abs(view.to - defaultView(domain).to) < 0.01);
  return (
    <div ref={box} className={`cone-chart${full.on ? " full" : ""}`} data-testid="cone-chart">
      <div className="cone-tools" role="toolbar" aria-label="Fill chart range and zoom">
        {ranges && (
          <div className="cone-ranges" role="group" aria-label="Range">
            {CONE_RANGES.map((item) => (
              <button
                key={item.key}
                type="button"
                className="cone-tool"
                aria-pressed={range === item.key}
                aria-label={item.label}
                title={item.label}
                data-testid={`cone-range-${item.key}`}
                onClick={() => pick(item.key, item.days)}
              >
                {item.short}
              </button>
            ))}
          </div>
        )}
        <div className="cone-moves" role="group" aria-label="Move and zoom">
          <button type="button" className="cone-tool" aria-label="Move back in time" title="Move back in time" data-testid="cone-pan-back" disabled={view.from <= domain.from + 0.01} onClick={() => panBy(-PAN_SHARE)}>
            <Icon d="M10 3 5 8l5 5" />
          </button>
          <button type="button" className="cone-tool" aria-label="Move forward in time" title="Move forward in time" data-testid="cone-pan-ahead" disabled={view.to >= domain.to - 0.01} onClick={() => panBy(PAN_SHARE)}>
            <Icon d="m6 3 5 5-5 5" />
          </button>
          <button type="button" className="cone-tool" aria-label="Zoom out" title="Zoom out" data-testid="cone-zoom-out" onClick={() => zoomBy(1 / ZOOM_STEP)}>
            <Icon d="M3 8h10" />
          </button>
          <button type="button" className="cone-tool" aria-label="Zoom in" title="Zoom in" data-testid="cone-zoom-in" onClick={() => zoomBy(ZOOM_STEP)}>
            <Icon d="M3 8h10M8 3v10" />
          </button>
          <button type="button" className="cone-tool cone-reset" data-testid="cone-reset" disabled={isDefault && range === null} onClick={reset}>
            Reset
          </button>
          {full.supported && (
            <button
              type="button"
              className="cone-tool"
              aria-label={full.on ? "Exit full screen" : "Show the chart in full screen"}
              title={full.on ? "Exit full screen" : "Show the chart in full screen"}
              aria-pressed={full.on}
              data-testid="cone-fullscreen"
              onClick={full.toggle}
            >
              <Icon d={full.on ? "M6 2v4H2M10 2v4h4M6 14v-4H2M10 14v-4h4" : "M2 6V2h4M14 6V2h-4M2 10v4h4M14 10v4h-4"} />
            </button>
          )}
        </div>
        {loading && (
          <span className="cone-status" role="status" data-testid="cone-loading">
            Loading the history…
          </span>
        )}
        {note && (
          <span className="cone-status" role="status" data-testid="cone-history-start">
            {note}
          </span>
        )}
        {failed && !loading && (
          <span className="cone-status error" role="status" data-testid="cone-failed">
            Could not load the longer history.
          </span>
        )}
      </div>
      <div
        ref={plot}
        className="cone-plot"
        tabIndex={0}
        role="group"
        aria-label={`Fill chart. ${KEY_TEXT}`}
        data-testid="cone-plot"
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={(e) => up(e)}
        onPointerCancel={(e) => up(e, true)}
        onPointerLeave={(e) => {
          if (e.pointerType === "mouse" && !drag.current) setCursor(null);
        }}
        onKeyDown={key}
        onBlur={() => setSaid("")}
      >
        <FillCone cone={cone} size="page" now={now} layout={{ view, width, height }} marks={{ cursor, selection }} />
      </div>
      <p className="cone-help" data-testid="cone-help">
        {touch ? (canPan ? TOUCH_TEXT : TOUCH_FIRST_TEXT) : HELP_TEXT}
      </p>
      <span className="visually-hidden" aria-live="polite" data-testid="cone-said">
        {said}
      </span>
    </div>
  );
}

/** Rule U-24: the full-size cone of the account page, with the history of the range that the user picks. */
export function AccountConeChart({ accountId }: { accountId: string }) {
  const [past, setPast] = useState(DEFAULT_PAST);
  const found = useCone(accountId, true, past === DEFAULT_PAST ? undefined : past);
  const kept = useRef<{ accountId: string; cone: api.FillCone } | null>(null);
  if (typeof found === "object" && found !== null) kept.current = { accountId, cone: found };
  const cone = kept.current?.accountId === accountId ? kept.current.cone : null;
  if (found === "none" && !cone) return <p className="sub" data-testid="account-cone-none">No estimate yet.</p>;
  if (!cone) return null;
  return <ConeChart cone={cone} loading={found === "loading"} failed={found === "failed" || found === "none"} onPast={(days) => setPast((was) => Math.max(was, days))} />;
}
